import { Injectable, NgZone } from '@angular/core';
import { IPieChartDataItem } from '../models/pie-chart-data-item';
import { Chart, DoughnutController, ArcElement, registerables } from 'chart.js';

@Injectable({
  providedIn: 'root',
})
export class ChartjsService {
  private chartInstance: Chart | null = null; // Store the chart instance

  constructor(public ngZone: NgZone) {}

  public renderDoughnutChart(
    canvas: HTMLCanvasElement,
    dataItems: IPieChartDataItem[],
    centerText: string
  ): Chart | null {

    // Destroy existing chart before creating a new one
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }

    const centerTextPlugin = {
      id: 'centerTextPlugin',
      afterDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx;
        const data = chart.data;
        const total = data.datasets[0].data.reduce((acc: any, val: any) => acc + val, 0);

        const midX =
          chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
        const midY =
          chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;

        ctx.save();

        // Use relative font sizes
        const bigFontSize = Math.min(chart.chartArea.width, chart.chartArea.height) * 0.25; // 25% of smaller dimension
        const smallFontSize = bigFontSize * 0.3; // 30% of big font size
        const positionOffset = Math.min(chart.chartArea.width, chart.chartArea.height) * 0.04;

        // Draw the big number
        ctx.fillStyle = 'black'; // Customize text color
        ctx.font = `bold ${bigFontSize}px Arial`; // Big number font size
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${total}`, midX, midY - positionOffset); // Position big number

        // Draw "centerText"
        ctx.font = `bold ${smallFontSize}px Arial`; // Text font size
        ctx.fillText(centerText, midX, midY + positionOffset * 3); // Position below big number

        ctx.restore();
      },
    };

    // Avoid multiple registrations of the plugin
    Chart.registry.plugins.unregister(centerTextPlugin);
    Chart.register(DoughnutController, ArcElement, centerTextPlugin, ...registerables);

    this.ngZone.runOutsideAngular(() => {
      const canvasContext = canvas?.getContext('2d');
      if (!canvasContext) return;

      this.chartInstance = new Chart(canvasContext, {
        type: 'doughnut',
        options: {
          animation: true,
          responsive: true,
          maintainAspectRatio: false,
          cutout: '80%',
          elements: {
            arc: {
              backgroundColor: dataItems.map((row) => row.color),
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  const label = context.label || '';
                  const value = context.raw || 0;
                  return `${label}: ${value} votes`;
                },
              },
            },
          },
        },
        data: {
          labels: dataItems.map((row) => row.label),
          datasets: [
            {
              label: 'Votes',
              data: dataItems.map((row) => row.count),
            },
          ],
        },
      });


    });
    return this.chartInstance;
  }
}
